<script setup lang="ts">
  import { MpLoadingContainer, MpText, pushToastMessage } from '@borg/ui';
  import JobApplicationSaveSearchSuccessToastSubtitle from '../JobApplicationSaveSearchSuccessToastSubtitle.vue';

  const { t } = useI18n();
  const applicationForm = _applicationForm.inject();
  const userStore = useUserStore();
  const savingSearch = ref(false);
  const creatingAccount = ref(false);

  const { data: recommendedJobs, pending } = await recommendedJobsRepository.getMany(
    'application-success-page',
    false,
    true,
  );

  async function onCreateAccount() {
    if (applicationForm.application?.id) {
      try {
        creatingAccount.value = true;
        await jobApplicationsService.createAccount(applicationForm.application.id);
        userStore.init();
        if (applicationForm.applicationActions?.shouldSaveSearch) {
          applicationForm.goTo('success-save-search');
        } else {
          applicationForm.goTo('success-recommended-jobs');
        }
        pushToastMessage({
          type: 'success',
          title: t('JOB_APPLICATION_SUCCESS_PAGE.CREATE_ACCOUNT_SUCCESS_TOAST.TITLE'),
          subtitle: '',
        });
      } catch (error) {
        pushToastMessage({
          type: 'alert',
          title: t('JOB_APPLICATION_SUCCESS_PAGE.CREATE_ACCOUNT_ERROR_TOAST.TITLE'),
          subtitle: t('JOB_APPLICATION_SUCCESS_PAGE.CREATE_ACCOUNT_ERROR_TOAST.SUBTITLE'),
        });
      } finally {
        creatingAccount.value = false;
      }
    }
  }

  async function onSaveSearch() {
    if (applicationForm.application?.id) {
      try {
        savingSearch.value = true;
        await jobApplicationsService.saveSearch(applicationForm.application.id);
        applicationForm.goTo('success-recommended-jobs');
        pushToastMessage({
          type: 'success',
          subtitle: h(JobApplicationSaveSearchSuccessToastSubtitle),
        });
      } catch (error) {
        pushToastMessage({
          type: 'alert',
          title: t('JOB_APPLICATION_SUCCESS_PAGE.SAVE_SEARCH_ERROR_TOAST.TITLE'),
          subtitle: t('JOB_APPLICATION_SUCCESS_PAGE.SAVE_SEARCH_ERROR_TOAST.SUBTITLE'),
        });
      } finally {
        savingSearch.value = false;
      }
    }
  }

  function onCancel() {
    applicationForm.goTo('success-recommended-jobs');
  }
</script>

<template>
  <div>
    <div class="application-success">
      <div class="application-success__img">
        <Illustration
          image="application-success"
          width="200"
          max-width="50vw"
          max-height="15rem"
        />
      </div>
      <MpText as="h2">
        {{ $t('JOB_APPLICATION_SUCCESS_PAGE.TITLE') }}
      </MpText>
    </div>
    <template v-if="applicationForm.application">
      <JobApplicationSuccessAction
        v-if="applicationForm.viewState === 'success-confirm-account'"
        :action-text="$t('JOB_APPLICATION_SUCCESS_PAGE.CREATE_ACCOUNT_ACTION_TEXT')"
        :confirm-text="$t('JOB_APPLICATION_SUCCESS_PAGE.CREATE_ACCOUNT_CONFIRM_BUTTON_LABEL')"
        :cancel-text="$t('JOB_APPLICATION_SUCCESS_PAGE.CREATE_ACCOUNT_CANCEL_BUTTON_LABEL')"
        :is-loading="creatingAccount"
        @confirm="onCreateAccount"
        @cancel="onCancel"
      />
      <JobApplicationSuccessAction
        v-if="applicationForm.viewState === 'success-save-search'"
        :action-text="$t('JOB_APPLICATION_SUCCESS_PAGE.SAVE_SEARCH_ACTION_TEXT')"
        :confirm-text="$t('JOB_APPLICATION_SUCCESS_PAGE.SAVE_SEARCH_CONFIRM_BUTTON_LABEL')"
        :cancel-text="$t('JOB_APPLICATION_SUCCESS_PAGE.SAVE_SEARCH_CANCEL_BUTTON_LABEL')"
        :is-loading="savingSearch"
        @confirm="onSaveSearch"
        @cancel="onCancel"
      />
    </template>
    <MpLoadingContainer
      v-if="applicationForm.viewState === 'success-recommended-jobs'"
      :is-loading="pending"
    >
      <div
        v-if="recommendedJobs && recommendedJobs.length"
        class="similar-jobs"
      >
        <MpText as="h4">
          {{ $t('JOB_APPLICATION_SUCCESS_PAGE.SHOW_RECOMMENDED_JOBS_SUBTITLE') }}
        </MpText>
        <Grid columns="two">
          <JobCardRecommended
            v-for="item in recommendedJobs"
            :key="item.id"
            :item="item"
            show-organization
            is-bordered-card
            source="applied_page_recommendations"
          />
        </Grid>
      </div>
    </MpLoadingContainer>
  </div>
</template>

<style scoped lang="scss">
  .application-success {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &__img {
      margin-bottom: var(--mp-space-60);
    }
  }

  .confirm-account-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--mp-space-60);
    margin-top: var(--mp-space-100);

    &__actions {
      display: flex;
      gap: var(--mp-space-20);
    }
  }

  .similar-jobs {
    margin-top: var(--mp-space-90);
    padding-bottom: var(--mp-space-60);
  }
</style>
